import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Laptop,
  Smartphone,
  Cpu,
  Plug,
  Wrench,
  Rocket,
  Check,
  Bolt,
  MapPin,
  Phone,
  Clock,
  Brain,
} from "lucide-react";
import "./TechAgency.css";
import emailjs from "@emailjs/browser";
import { subscribeToNewsletter } from "./newsletterService";
const heroBackground = "/hero-background.jpg";

const EMAILJS_CONFIG = {
  SERVICE_ID: process.env.REACT_APP_EMAILJS_SERVICE_ID,
  TEMPLATE_ID: process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
  PUBLIC_KEY: process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
};

export default function TechAgency() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    projectType: "",
    budget: "",
    timeline: "",
    message: "",
    appointmentDate: "",
    appointmentTime: "",
    consultationType: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({
    success: false,
    message: "",
  });
  const TESTIMONIALS_STORAGE_KEY = "techxm_testimonials";
  const FAQ_STORAGE_KEY = "techxm_faqs";

  useEffect(() => {
    // Initialize EmailJS with new syntax
    if (EMAILJS_CONFIG.PUBLIC_KEY) {
      emailjs.init({
        publicKey: EMAILJS_CONFIG.PUBLIC_KEY,
        blockHeadless: true,
      });
    }

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const fadeElements = document.querySelectorAll(".fade-in");

    const fadeInOnScroll = function () {
      fadeElements.forEach((element) => {
        const elementTop = element.getBoundingClientRect().top;
        const elementVisible = 150;

        if (elementTop < window.innerHeight - elementVisible) {
          element.style.opacity = "1";
          element.style.transform = "translateY(0)";
        }
      });
    };

    fadeElements.forEach((element) => {
      element.style.opacity = "0";
      element.style.transform = "translateY(20px)";
      element.style.transition = "opacity 0.8s ease, transform 0.8s ease";
    });

    window.addEventListener("scroll", fadeInOnScroll);
    fadeInOnScroll();

    return () => window.removeEventListener("scroll", fadeInOnScroll);
  }, []);

  useEffect(() => {
    try {
      const stored = JSON.parse(
        localStorage.getItem(TESTIMONIALS_STORAGE_KEY) || "[]",
      );
      if (Array.isArray(stored) && stored.length > 0) {
        setTestimonials(stored);
      }
    } catch (error) {
      console.error("Failed to load testimonials:", error);
    }
  }, []);

  const services = [
    {
      icon: <Laptop className="service-icon-svg" />,
      title: "Web Development Services",
      description:
        "Custom websites and web applications built with modern technologies and best practices.",
      hoverTitle: "Web Development Excellence",
      hoverDescription:
        "We create responsive, high-performance websites and web applications tailored to your business needs.",
      features: [
        "Custom Website Development",
        "E-commerce Solutions",
        "Web Application Development",
        "Responsive Design",
      ],
      link: "/web-development",
    },
    {
      icon: <Smartphone className="service-icon-svg" />,
      title: "Mobile App Development",
      description:
        "Native and cross-platform mobile applications for iOS and Android platforms.",
      hoverTitle: "Mobile App Innovation",
      hoverDescription:
        "From concept to deployment, we build mobile applications that users love with intuitive interfaces.",
      features: [
        "iOS & Android Native Apps",
        "React Native Development",
        "App Store Optimization",
        "Push Notification Systems",
      ],
      link: "/mobile-app",
    },
    {
      icon: <Cpu className="service-icon-svg" />,
      title: "Custom Software Development",
      description:
        "Tailored software solutions built from the ground up for your unique business requirements.",
      hoverTitle: "Custom Software Solutions",
      hoverDescription:
        "We develop custom software that solves specific business challenges with scalable, secure solutions.",
      features: [
        "Enterprise Software",
        "CRM & ERP Systems",
        "Business Process Automation",
        "Custom Database Solutions",
      ],
      link: "/custom-software",
    },
    {
      icon: <Plug className="service-icon-svg" />,
      title: "API & Backend Services",
      description:
        "Robust backend systems and API integrations to power your applications.",
      hoverTitle: "Backend Excellence",
      hoverDescription:
        "We build secure, scalable backend systems and seamless API integrations for your applications.",
      features: [
        "REST & GraphQL APIs",
        "Database Design & Optimization",
        "Third-party API Integration",
        "Cloud Infrastructure",
      ],
      link: "/api-backend",
    },
    {
      icon: <Wrench className="service-icon-svg" />,
      title: "Website & App Maintenance",
      description:
        "Ongoing support, updates, and maintenance to keep your digital products running smoothly.",
      hoverTitle: "Reliable Maintenance",
      hoverDescription:
        "We ensure your applications are always available, secure, and performing at their best.",
      features: [
        "Regular Security Updates",
        "Performance Optimization",
        "Bug Fixes & Support",
        "Feature Enhancements",
      ],
      link: "/maintenance",
    },
    {
      icon: <Brain className="service-icon-svg" />,
      title: "AI & Machine Learning Services",
      description:
        "Custom AI solutions, intelligent chatbots, predictive models, and automation built to deliver measurable business outcomes.",
      hoverTitle: "AI-Powered Innovation",
      hoverDescription:
        "We build intelligent systems that learn from your data, automate complex tasks, and drive growth through applied artificial intelligence.",
      features: [
        "Custom AI Chatbots & Assistants",
        "Machine Learning Models",
        "Predictive Analytics",
        "AI-Powered Automation",
      ],
      link: "/ai-services",
    },
  ];

  const stats = [
    { number: "15+", label: "Projects Delivered" },
    { number: "100%", label: "Client Satisfaction" },
    { number: "6", label: "Expert Team Members" },
    { number: "2+", label: "Years Experience" },
  ];

  const trustSignals = [
    "Executive-level visibility",
    "Built to scale",
    "Ongoing support & governance",
    "Outcomes you can measure",
  ];

  const valueProps = [
    {
      icon: <Rocket className="value-prop-icon-svg" />,
      title: "Reliable Delivery Execution",
      description:
        "We deliver production-ready platforms through disciplined planning, predictable milestones, and rigorous quality control.",
    },
    {
      icon: <Bolt className="value-prop-icon-svg" />,
      title: "Performance and Stability",
      description:
        "From frontend responsiveness to backend reliability, our systems are engineered for consistent long-term operation.",
    },
    {
      icon: <Check className="value-prop-icon-svg" />,
      title: "Strategic Business Alignment",
      description:
        "Every major delivery decision is aligned to business outcomes such as operational efficiency, customer growth, and retention.",
    },
  ];

  const defaultTestimonials = [
    {
      content:
        "TECHXM delivered beyond our expectations. Their team was professional, responsive, and delivered a high-quality product on time.",
      author: "Michael Johnson",
      position: "CEO, TechSolutions Inc.",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg",
    },
    {
      content:
        "The mobile app developed by TECHXM has significantly improved our customer engagement. Their attention to detail is impressive.",
      author: "Sarah Williams",
      position: "Product Manager, HealthPlus",
      avatar: "https://randomuser.me/api/portraits/women/44.jpg",
    },
    {
      content:
        "Working with TECHXM was a game-changer for our business. Their technical expertise and creative solutions helped us scale rapidly.",
      author: "David Chen",
      position: "Founder, FinTech Innovations",
      avatar: "https://randomuser.me/api/portraits/men/67.jpg",
    },
  ];
  const [, setTestimonials] = useState(defaultTestimonials);

  const defaultFaqs = [
    {
      question: "What are your pricing models?",
      answer:
        "We offer flexible pricing models including fixed-price projects for well-defined scopes, time-and-materials for evolving projects, and monthly retainers for ongoing support and maintenance. We provide detailed quotes after understanding your specific requirements.",
    },
    {
      question: "How long does a typical project take?",
      answer:
        "Project timelines vary based on complexity. Simple websites take 2-4 weeks, mobile apps 6-12 weeks, and custom software solutions 8-16 weeks. We provide detailed project timelines during our initial consultation and maintain regular progress updates.",
    },
    {
      question: "Do you provide ongoing support and maintenance?",
      answer:
        "Yes, we offer comprehensive support packages including bug fixes, security updates, performance optimization, and feature enhancements. Our support plans are tailored to your specific needs and include 24/7 monitoring for critical applications.",
    },
    {
      question: "What technologies do you work with?",
      answer:
        "We specialize in modern technologies including React, React Native, Node.js, Python, and cloud platforms like AWS and Firebase. We choose the best technology stack for each project based on requirements, scalability needs, and long-term maintainability.",
    },
    {
      question: "Can you work with our existing team?",
      answer:
        "Absolutely! We frequently collaborate with in-house teams, providing specialized expertise where needed. We're experienced in agile methodologies and can integrate seamlessly with your existing workflows and development processes.",
    },
    {
      question: "What is your process for starting a new project?",
      answer:
        "Our process begins with a discovery phase where we deeply understand your requirements, followed by planning, design, development, testing, and deployment. We maintain transparent communication throughout and involve you in key decision-making milestones.",
    },
  ];
  const [faqs, setFaqs] = useState(defaultFaqs);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(FAQ_STORAGE_KEY) || "[]");
      if (Array.isArray(stored) && stored.length > 0) {
        setFaqs(stored);
      }
    } catch (error) {
      console.error("Failed to load FAQs:", error);
    }
  }, []);

  const consultationTypes = [
    "Strategy Session (30 min)",
    "Technical Consultation (60 min)",
    "Project Kickoff (90 min)",
    "Quick Q&A (15 min)",
  ];

  const handleFaqClick = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const cleanEmailValue = (value, fallback = "N/A") => {
    const normalized = String(value ?? "").trim();
    return normalized || fallback;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ success: false, message: "" });

    if (
      !EMAILJS_CONFIG.SERVICE_ID ||
      !EMAILJS_CONFIG.TEMPLATE_ID ||
      !EMAILJS_CONFIG.PUBLIC_KEY
    ) {
      setSubmitStatus({
        success: false,
        message: "Email service is not configured. Please try again later.",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const requiredFields = {
        name: cleanEmailValue(formData.name, ""),
        email: cleanEmailValue(formData.email, ""),
        phone: cleanEmailValue(formData.phone, ""),
        appointmentDate: cleanEmailValue(formData.appointmentDate, ""),
        appointmentTime: cleanEmailValue(formData.appointmentTime, ""),
        consultationType: cleanEmailValue(formData.consultationType, ""),
        projectType: cleanEmailValue(formData.projectType, ""),
        message: cleanEmailValue(formData.message, ""),
      };

      const missingFields = Object.entries(requiredFields)
        .filter(([, value]) => !value)
        .map(([key]) => key);

      if (missingFields.length > 0) {
        throw new Error("Please complete all required fields before submitting.");
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(requiredFields.email)) {
        throw new Error("Please enter a valid email address.");
      }

      const templateParams = {
        from_name: requiredFields.name,
        from_email: requiredFields.email,
        to_email: "info@techxm.co.za",
        name: requiredFields.name,
        email: requiredFields.email,
        title: "New Appointment Request",
        time: new Date().toLocaleString("en-ZA", {
          dateStyle: "medium",
          timeStyle: "short",
        }),
        phone: requiredFields.phone,
        company: cleanEmailValue(formData.company),
        appointment_date: requiredFields.appointmentDate,
        appointment_time: requiredFields.appointmentTime,
        consultation_type: requiredFields.consultationType,
        date_submitted: new Date().toLocaleString("en-ZA", {
          dateStyle: "medium",
          timeStyle: "short",
        }),
        reply_to: requiredFields.email,
        project_type: requiredFields.projectType,
        budget: cleanEmailValue(formData.budget, "Not specified"),
        timeline: cleanEmailValue(formData.timeline, "Not specified"),
        message: requiredFields.message,
        source: "TECHXM website contact form",
        form_type: "Appointment request",
        submission_id: `TECHXM-${Date.now()}`,
      };

      console.table(templateParams);

      await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        templateParams,
      );

      // Show success message
      setSubmitStatus({
        success: true,
        message:
          "Thank you! Your appointment request has been sent successfully. Check your email inbox (and spam folder). We'll also contact you within 24 hours to confirm.",
      });

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        projectType: "",
        budget: "",
        timeline: "",
        message: "",
        appointmentDate: "",
        appointmentTime: "",
        consultationType: "",
      });
    } catch (error) {
      console.error("EmailJS Error:", error);
      console.error("Error Status:", error?.status);
      console.error("Error Text:", error?.text);

      let errorMessage = "Something went wrong. ";

      if (error?.text?.includes("insufficient authentication")) {
        errorMessage =
          "Email service needs reauthorization. Please contact support.";
      } else if (error?.text?.includes("412")) {
        errorMessage = "Template configuration issue. Please try again.";
      } else {
        errorMessage +=
          "The form showed success but email may not have been delivered. Please contact us directly at admin@techxm.co.za";
      }

      setSubmitStatus({
        success: false,
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const startProject = () => {
    scrollToSection("contact");
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const email = form.newsletterEmail?.value || "";
    const result = subscribeToNewsletter(email);
    window.alert(result.message);
    if (result.ok) {
      form.reset();
    }
  };

  return (
    <div className="tech-agency">
      <nav className={`navbar ${isScrolled ? "scrolled" : ""}`}>
        <div className="container navbar-container">
                <a href="/" className="navbar-brand">
            <img
              src="/TechXM-logo.png"
              alt="TECHXM logo"
              className="brand-logo-mark"
              width="30"
              height="30"
            />
            
          </a>

          <div className={`navbar-menu ${mobileMenuOpen ? "active" : ""}`}>
            <div className="services-dropdown">
              <div className="navbar-link">Services</div>
              <div className="dropdown-menu">
                <Link to="/web-development" className="dropdown-item">
                  <i className="fas fa-laptop-code"></i>
                  Web Development Services
                </Link>
                <Link to="/mobile-app" className="dropdown-item">
                  <i className="fas fa-mobile-alt"></i>
                  Mobile App Development
                </Link>
                <Link to="/custom-software" className="dropdown-item">
                  <i className="fas fa-cogs"></i>
                  Custom Software Development
                </Link>
                <Link to="/api-backend" className="dropdown-item">
                  <i className="fas fa-plug"></i>
                  API & Backend Services
                </Link>
                <Link to="/maintenance" className="dropdown-item">
                  <i className="fas fa-tools"></i>
                  Website & App Maintenance
                </Link>
                <Link to="/ai-services" className="dropdown-item">
                  <i className="fas fa-robot"></i>
                  AI & Machine Learning Services
                </Link>
              </div>
            </div>
            {/* <a href="/#work" className="navbar-link">Work</a> */}
            <a href="/#about" className="navbar-link">
              About
            </a>
            {/*
            <a href="/#testimonials" className="navbar-link">
              Testimonials
            </a>
            */}
            <a href="/#faq" className="navbar-link">
              FAQ
            </a>
            <a href="/#contact" className="navbar-link">
              Contact
            </a>
            <Link to="/track-project" className="navbar-link">
              Track Project
            </Link>
            <button className="btn btn-primary nav-consult-btn" onClick={startProject}>
              Request Consultation
            </button>
          </div>

          <button
            className="navbar-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`fas ${mobileMenuOpen ? "fa-times" : "fa-bars"}`}></i>
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section
        className="hero"
        style={{
          backgroundImage: `url(${heroBackground})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="container hero-content fade-in">
          <div className="hero-badge">
            Enterprise Engineering Partner
          </div>
          <h1 className="hero-title">
            <span className="hero-title-line hero-title-line-1">
              Enterprise Software,
            </span>
            <span className="hero-title-line hero-title-line-2">
              <span className="hero-title-accent">Engineered for Scale</span>
            </span>
          </h1>
          <p>
            We design, build, and support secure digital platforms that help
            growing organizations move faster and operate with confidence.
          </p>
          <div className="hero-proof-list">
            {trustSignals.map((item, index) => (
              <div key={index} className="hero-proof-item">
                <i className="fas fa-check-circle"></i>
                <span>{item}</span>
              </div>
            ))}
          </div>
          <div className="hero-buttons">
            <button className="btn btn-primary" onClick={startProject}>
              Schedule a Consultation
              <i className="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </section>

      <section className="trust-strip">
        <div className="container trust-strip-container">
          <span className="trust-strip-label">Core Delivery Stack</span>
          <div className="trust-strip-items">
            <span>React</span>
            <span>React Native</span>
            <span>Node.js</span>
            <span>.NET</span>
            <span>AWS</span>
            <span>Firebase</span>
          </div>
        </div>
      </section>

      <section className="section stats">
        <div className="container">
          <div className="stats-container">
            {stats.map((stat, index) => (
              <div
                key={index}
                className={`stat-item fade-in ${
                  index > 0 ? `delay-${index}` : ""
                }`}
              >
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section value-props">
        <div className="container">
          <div className="section-title">
            <h2>Why Enterprises Choose TECHXM</h2>
            <p>
              A delivery partner focused on governance, quality, and measurable
              operational impact.
            </p>
          </div>
          <div className="value-props-grid">
            {valueProps.map((item, index) => (
              <article key={index} className="value-prop-card card fade-in">
                <div className="value-prop-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section about" id="about">
        <div className="container">
          <div className="about-content fade-in">
            <div className="about-text">
              <h2>About TECHXM</h2>
              <p>
                TECHXM is a Johannesburg, South African web development and
                software company trusted by growth-focused teams. We combine
                product strategy, engineering discipline, and modern design
                standards to deliver solutions that are dependable and scalable.
              </p>
              <p>
                Our team works across web, mobile, backend, and platform
                architecture to deliver end-to-end solutions with clear
                communication, transparent delivery, and long-term support for
                businesses serving Johannesburg and the rest of South Africa.
              </p>
              <button className="btn btn-primary" onClick={startProject}>
                Request Consultation
                <i className="fas fa-arrow-right"></i>
              </button>
            </div>
            <div className="about-image">
              <img
                src="/about-team.jpg"
                alt="TECHXM team collaborating in a modern office"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="section" id="services">
        {/* Subtle brand-gradient wash */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background:
              "linear-gradient(135deg, rgba(var(--color-primary-rgb), 0.03) 0%, rgba(var(--color-secondary-rgb), 0.03) 100%)",
            zIndex: 0,
          }}
        />

        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <div className="section-title">
            <h2>Our Services</h2>
            <p>
              Web design Johannesburg organizations rely on, plus website
              development South African teams use to modernize operations and
              grow.
            </p>
          </div>

          <div className="services-grid">
            {services.map((service, index) => (
              <Link
                to={service.link}
                key={index}
                className={`card service-card fade-in ${
                  index > 0 ? `delay-${index}` : ""
                }`}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <div className="service-icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>

                <div className="service-hover-content">
                  <h4>{service.hoverTitle}</h4>
                  <p>{service.hoverDescription}</p>
                  <div className="service-features">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="service-feature">
                        <i className="fas fa-check"></i>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    className="btn btn-primary"
                    style={{ background: "white", color: "var(--primary)" }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      startProject();
                    }}
                  >
                    Learn More
                    <i className="fas fa-arrow-right"></i>
                  </button>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/*
      <section className="section projects" id="work">
        <div className="container">
          <div className="section-title">
            <h2>Featured Projects</h2>
            <p>
              Selected delivery engagements across web, mobile, and platform
              systems
            </p>
          </div>

          <div className="projects-grid">
            {projects.map((project, index) => (
              <div
                key={index}
                className={`card project-card fade-in ${
                  expandedProject === index ? "expanded" : ""
                } ${index > 0 ? `delay-${index}` : ""}`}
              >
                <button
                  type="button"
                  className="project-media"
                  onClick={() => handleProjectClick(index)}
                  aria-expanded={expandedProject === index}
                >
                  <div
                    className="project-media-bg"
                    style={{
                      background: project.projectImage
                        ? `linear-gradient(rgba(7, 20, 39, 0.45), rgba(7, 20, 39, 0.45)), url(${project.projectImage}) center/cover no-repeat`
                        : project.gradient,
                    }}
                  />
                  <div className="project-media-content">
                    <span className="project-media-category">
                      {project.category}
                    </span>
                    <h3>{project.title}</h3>
                    <span className="project-expand-chip">
                      {expandedProject === index
                        ? "Hide details"
                        : "View details"}
                      {expandedProject === index ? (
                        <ChevronUp size={16} />
                      ) : (
                        <ChevronDown size={16} />
                      )}
                    </span>
                  </div>
                </button>

                <div className="project-content">
                  <p>{project.description}</p>
                  <div className="project-tech">
                    {project.technologies.map((tech, techIndex) => (
                      <span key={techIndex} className="tech-tag">
                        {tech}
                      </span>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="project-toggle-btn"
                    onClick={() => handleProjectClick(index)}
                  >
                    {expandedProject === index
                      ? "Collapse project"
                      : "Expand project"}
                    {expandedProject === index ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </button>
                </div>

                <div
                  className={`project-details-panel ${
                    expandedProject === index ? "open" : ""
                  }`}
                  ref={(el) => {
                    projectDetailsRefs.current[index] = el;
                  }}
                >
                  <div className="project-details-inner">
                    <div>
                      <h4>Key Features</h4>
                      <ul className="project-features">
                        {project.features.map((feature, featureIndex) => (
                          <li key={featureIndex}>{feature}</li>
                        ))}
                      </ul>
                      <button
                        className="btn btn-primary"
                        onClick={startProject}
                      >
                        Discuss a Similar Engagement
                        <i className="fas fa-arrow-right"></i>
                      </button>
                    </div>

                    <div>
                      <h4>Project Screens</h4>
                      <div className="project-screenshots">
                        {project.screenshots.map(
                          (screenshot, screenshotIndex) => (
                            <div
                              key={screenshotIndex}
                              className="project-screenshot"
                            >
                              <img
                                src={screenshot.url}
                                alt={screenshot.caption}
                              />
                              <div className="screenshot-caption">
                                {screenshot.caption}
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* <section className="section" id="testimonials">
        <div className="container">
          <div className="section-title">
            <h2>Client Testimonials</h2>
            <p>What our clients say about working with us</p>
          </div>

          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className={`card testimonial-card fade-in ${
                  index > 0 ? `delay-${index}` : ""
                }`}
              >
                <div className="testimonial-content">{testimonial.content}</div>
                <div className="testimonial-author">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.author}
                    className="testimonial-avatar"
                  />
                  <div className="testimonial-info">
                    <h4>{testimonial.author}</h4>
                    <p>{testimonial.position}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      <section className="section" id="faq">
        <div className="container">
          <div className="section-title">
            <h2>Frequently Asked Questions</h2>
            <p>
              Get answers to common questions about our services and process
            </p>
          </div>

          <div className="faq-grid">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`faq-item fade-in ${
                  expandedFaq === index ? "expanded" : ""
                } ${index > 0 ? `delay-${index % 3}` : ""}`}
              >
                <div
                  className="faq-question"
                  onClick={() => handleFaqClick(index)}
                >
                  <h4>{faq.question}</h4>
                  <i className="fas fa-chevron-down faq-toggle"></i>
                </div>
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section contact" id="contact">
        <div className="container">
          <div className="contact-header fade-in">
            <div className="section-title">
              <h2>Schedule a Consultation</h2>
              <p>
                Schedule a consultation with our team to review your goals,
                constraints, and delivery priorities. We will respond within 24
                hours to confirm your appointment.
              </p>
            </div>
          </div>

          <div className="contact-layout fade-in">
            <div className="contact-form-container">
              <form className="contact-form" onSubmit={handleSubmit}>
                {submitStatus.message && (
                  <div
                    className={`form-message ${
                      submitStatus.success ? "success" : "error"
                    }`}
                  >
                    <i
                      className={`fas ${
                        submitStatus.success
                          ? "fa-check-circle"
                          : "fa-exclamation-circle"
                      }`}
                    ></i>
                    {submitStatus.message}
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Your full name"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="your.email@example.com"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="+27 12 345 6789"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="company">Company</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="Your company name"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="appointmentDate">Preferred Date *</label>
                    <input
                      type="date"
                      id="appointmentDate"
                      name="appointmentDate"
                      value={formData.appointmentDate}
                      onChange={handleInputChange}
                      required
                      min={new Date().toISOString().split("T")[0]}
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="appointmentTime">Preferred Time *</label>
                    <select
                      id="appointmentTime"
                      name="appointmentTime"
                      value={formData.appointmentTime}
                      onChange={handleInputChange}
                      required
                      disabled={isSubmitting}
                    >
                      <option value="">Select preferred time</option>
                      <option value="09:00">09:00 AM</option>
                      <option value="10:00">10:00 AM</option>
                      <option value="11:00">11:00 AM</option>
                      <option value="12:00">12:00 PM</option>
                      <option value="14:00">02:00 PM</option>
                      <option value="15:00">03:00 PM</option>
                      <option value="16:00">04:00 PM</option>
                      <option value="17:00">05:00 PM</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="consultationType">Consultation Type *</label>
                  <div className="consultation-type-grid">
                    {consultationTypes.map((type, index) => (
                      <div key={index} className="consultation-type-option">
                        <input
                          type="radio"
                          id={`consultation-${index}`}
                          name="consultationType"
                          value={type}
                          checked={formData.consultationType === type}
                          onChange={handleInputChange}
                          required
                          disabled={isSubmitting}
                        />
                        <label htmlFor={`consultation-${index}`}>{type}</label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>What type of project are you interested in? *</label>
                  <div className="project-type-grid">
                    <div className="project-type-option">
                      <input
                        type="radio"
                        id="web-dev"
                        name="projectType"
                        value="Web Development"
                        checked={formData.projectType === "Web Development"}
                        onChange={handleInputChange}
                        required
                        disabled={isSubmitting}
                      />
                      <label htmlFor="web-dev">Web Development</label>
                    </div>
                    <div className="project-type-option">
                      <input
                        type="radio"
                        id="mobile-app"
                        name="projectType"
                        value="Mobile App"
                        checked={formData.projectType === "Mobile App"}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                      />
                      <label htmlFor="mobile-app">Mobile App</label>
                    </div>
                    <div className="project-type-option">
                      <input
                        type="radio"
                        id="custom-software"
                        name="projectType"
                        value="Custom Software"
                        checked={formData.projectType === "Custom Software"}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                      />
                      <label htmlFor="custom-software">Custom Software</label>
                    </div>
                    <div className="project-type-option">
                      <input
                        type="radio"
                        id="maintenance"
                        name="projectType"
                        value="Maintenance"
                        checked={formData.projectType === "Maintenance"}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                      />
                      <label htmlFor="maintenance">Maintenance</label>
                    </div>
                    <div className="project-type-option">
                      <input
                        type="radio"
                        id="other"
                        name="projectType"
                        value="Other"
                        checked={formData.projectType === "Other"}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                      />
                      <label htmlFor="other">Other</label>
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="budget">Estimated Budget</label>
                    <select
                      id="budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                    >
                      <option value="">Select budget range</option>
                      <option value="Under R50,000">Under R50,000</option>
                      <option value="R50,000 - R100,000">
                        R50,000 - R100,000
                      </option>
                      <option value="R100,000 - R250,000">
                        R100,000 - R250,000
                      </option>
                      <option value="R250,000+">R250,000+</option>
                      <option value="To be discussed">To be discussed</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="timeline">Project Timeline</label>
                    <select
                      id="timeline"
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                    >
                      <option value="">Select timeline</option>
                      <option value="ASAP">ASAP</option>
                      <option value="1-2 months">1-2 months</option>
                      <option value="3-6 months">3-6 months</option>
                      <option value="6+ months">6+ months</option>
                      <option value="Flexible">Flexible</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Project Details *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    placeholder="Tell us about your project requirements, goals, and any specific features you need..."
                    disabled={isSubmitting}
                  ></textarea>
                </div>

                <div className="form-agreement">
                  <input
                    type="checkbox"
                    id="agreement"
                    name="agreement"
                    required
                    disabled={isSubmitting}
                  />
                  <label htmlFor="agreement">
                    I agree to receive follow-up emails regarding my appointment
                    request
                  </label>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ width: "100%" }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Sending Appointment Request...
                    </>
                  ) : (
                    <>
                      Schedule Appointment
                      <i className="fas fa-calendar-check"></i>
                    </>
                  )}
                </button>

                <p className="form-note">
                  <i className="fas fa-info-circle"></i>
                  We'll contact you within 24 hours to confirm your appointment
                  time.
                </p>
              </form>
            </div>

            <aside className="contact-location-panel" aria-label="TECHXM location">
              <div className="contact-location-content">
                <span className="contact-eyebrow">Visit TECHXM</span>
                <h3>Find us in Walkerville</h3>
                <p>
                  Meet with our team at our business location for discovery
                  sessions, technical planning, and project consultations.
                </p>

                <div className="contact-detail-list">
                  <div className="contact-detail-item">
                    <MapPin className="contact-detail-icon" aria-hidden="true" />
                    <div>
                      <span className="contact-detail-label">Address</span>
                      <strong>Ohenimuri, Walkerville</strong>
                    </div>
                  </div>

                  <div className="contact-detail-item">
                    <Phone className="contact-detail-icon" aria-hidden="true" />
                    <div>
                      <span className="contact-detail-label">Call us</span>
                      <strong>
                        <a href="tel:0765536048">076 553 6048</a>
                        <span> / </span>
                        <a href="tel:0742686212">074 268 6212</a>
                      </strong>
                    </div>
                  </div>

                  <div className="contact-detail-item">
                    <Clock className="contact-detail-icon" aria-hidden="true" />
                    <div>
                      <span className="contact-detail-label">Office hours</span>
                      <strong>Mon - Fri: 9:00 - 18:00</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div className="contact-map-card">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3233.0662110295757!2d27.94842977509312!3d-26.434625173397443!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1e9501cc237b5da1%3A0xa3fad09162a974ba!2sTechXM!5e1!3m2!1sen!2sza!4v1778939811232!5m2!1sen!2sza"
                  title="TECHXM location on Google Maps"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="section cta">
        <div className="container">
          <div className="cta-card card fade-in">
            <h2>Plan Your Next Digital Initiative</h2>
            <p>
              Discuss your roadmap with our team and get a clear delivery plan
              tailored to your Johannesburg or South African business objectives.
            </p>
            <button className="btn btn-primary" onClick={startProject}>
              Request Consultation
            </button>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="footer-decoration"></div>
        <div className="footer-decoration"></div>

        <div className="container">
          <div className="footer-grid">
            <div>
              <div className="footer-brand">
                <img
                  src="/TechXM-logo.png"
                  alt="TECHXM logo"
                  className="brand-logo-mark"
                  width="30"
                  height="30"
                />
              </div>
              <p className="footer-about">
                We design, build, and support secure digital systems as a South
                African web development company serving businesses across
                Johannesburg and beyond.
              </p>
              <div className="footer-social">
                <a
                  href="https://www.linkedin.com/company/techxm/"
                  className="social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fab fa-linkedin"></i>
                </a>
                <a
                  href="https://www.instagram.com/techxm_za/"
                  className="social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fab fa-instagram"></i>
                </a>
                <a
                  href="https://www.facebook.com/"
                  className="social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fab fa-facebook"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="footer-heading">Services</h4>
              <ul className="footer-links">
                <li>
                  <a href="/web-development">
                    <i className="fas fa-chevron-right"></i>
                    Web Development
                  </a>
                </li>
                <li>
                  <a href="/mobile-app">
                    <i className="fas fa-chevron-right"></i>
                    Mobile Apps
                  </a>
                </li>
                <li>
                  <a href="/custom-software">
                    <i className="fas fa-chevron-right"></i>
                    Custom Software
                  </a>
                </li>
                <li>
                  <a href="/api-backend">
                    <i className="fas fa-chevron-right"></i>
                    API & Backend
                  </a>
                </li>
                <li>
                  <a href="/maintenance">
                    <i className="fas fa-chevron-right"></i>
                    Maintenance
                  </a>
                </li>
                <li>
                  <a href="/ai-services">
                    <i className="fas fa-chevron-right"></i>
                    AI & Machine Learning
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="footer-heading">Company</h4>
              <ul className="footer-links">
                <li>
                  <a href="/#about">
                    <i className="fas fa-chevron-right"></i>
                    About
                  </a>
                </li>
                <li>
                  <a href="/#faq">
                    <i className="fas fa-chevron-right"></i>
                    FAQ
                  </a>
                </li>
                <li>
                  <a href="/track-project">
                    <i className="fas fa-chevron-right"></i>
                    Track Project
                  </a>
                </li>
                {/*
                <li>
                  <a href="/#testimonials">
                    <i className="fas fa-chevron-right"></i>
                    Testimonials
                  </a>
                </li>
                */}
                <li>
                  <a href="/#contact">
                    <i className="fas fa-chevron-right"></i>
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="footer-heading">Contact Info</h4>
              <div className="footer-contact">
                <div className="contact-item">
                  <i className="fas fa-map-marker-alt"></i>
                  <span>Ohenimuri , Walkerville</span>
                </div>
                <div className="contact-item">
                  <i className="fas fa-phone"></i>
                  <a href="tel:0765536048">076 553 6048</a>
                  <span> / </span>
                  <a href="tel:0742686212">074 268 6212</a>
                </div>
                <div className="contact-item">
                  <i className="fas fa-envelope"></i>
                  <a href="mailto:info@techxm.co.za">info@techxm.co.za</a>
                </div>
                <div className="contact-item">
                  <i className="fas fa-clock"></i>
                  <span>Mon - Fri: 9:00 - 18:00</span>
                </div>
              </div>

              <div className="footer-newsletter">
                <p className="newsletter-text">Subscribe to our newsletter</p>
                <form
                  className="newsletter-form"
                  onSubmit={handleNewsletterSubmit}
                >
                  <input
                    type="email"
                    name="newsletterEmail"
                    className="newsletter-input"
                    placeholder="Your email address"
                    required
                  />
                  <button type="submit" className="newsletter-btn">
                    <i className="fas fa-paper-plane"></i>
                  </button>
                </form>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>
              &copy; {new Date().getFullYear()} TECHXM. All rights reserved.
              Premium Digital Solutions.
            </p>
            <div className="footer-legal">
              <a href="/privacy-policy">Privacy Policy</a>
              <a href="/terms-of-service">Terms of Service</a>
              <a href="/cookie-policy">Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}


